/** @jsx jsx */
import { jsx } from '@emotion/core'
import { ExternalStyles } from 'bold-ui'
import { composeRefs } from 'bold-ui/lib/util/react'
import { noop } from 'lodash'
import { useCallback, useEffect, useMemo, useRef, VideoHTMLAttributes } from 'react'
import { usePictureInPicture } from 'view/videochamada/hooks/usePictureInPicture'
import { EventVideoChange, MediaStreamTrackKind } from 'view/videochamada/model-videochamada'

interface StreamPlayerProps extends Omit<VideoHTMLAttributes<HTMLVideoElement>, 'style'> {
  stream: MediaStream
  pipActive?: boolean
  setPipActive?(newVal: boolean): void
  style?: ExternalStyles
  onVideoResize?(videoWidth: number, videoHeight: number): void
}

export function StreamPlayer(props: Readonly<StreamPlayerProps>) {
  const { stream, style, pipActive = false, setPipActive = noop, onVideoResize, ...rest } = props
  const videoRef = useRef<HTMLVideoElement>()

  const setVideoRef = useCallback(
    (ref: HTMLVideoElement) => {
      videoRef.current = ref
      if (videoRef.current) videoRef.current.srcObject = stream
    },
    [stream]
  )

  useEffect(() => {
    const checkVideoOrientation = (e: EventVideoChange) => {
      onVideoResize?.(e.target?.videoWidth, e.target?.videoHeight)
    }

    const videoRefCurrent = videoRef.current
    if (videoRefCurrent) {
      videoRefCurrent.addEventListener('loadedmetadata', checkVideoOrientation)
      videoRefCurrent.addEventListener('resize', checkVideoOrientation)

      return () => {
        videoRefCurrent.removeEventListener('loadedmetadata', checkVideoOrientation)
        videoRefCurrent.removeEventListener('resize', checkVideoOrientation)
      }
    }
  }, [onVideoResize, videoRef])

  useEffect(() => {
    const handleChangeTracks = () => {
      const possuiTrackVideo = stream
        ?.getTracks()
        .some((track) => track.kind === MediaStreamTrackKind.VIDEO && track.enabled)
      if (pipActive && !possuiTrackVideo) setPipActive(false)
    }

    if (!stream && pipActive) setPipActive(false)
    stream?.getTracks().forEach(() => handleChangeTracks())

    stream?.addEventListener('enable', () => handleChangeTracks())
    return () => {
      stream?.removeEventListener('enable', () => handleChangeTracks())
    }
  }, [pipActive, setPipActive, stream])

  const { videoRef: pipVideoRef } = usePictureInPicture({ active: pipActive, onPipActiveChange: setPipActive })

  const ref = useMemo(() => composeRefs(setVideoRef, pipVideoRef), [pipVideoRef, setVideoRef])

  useEffect(() => {
    if (videoRef.current) videoRef.current.srcObject = stream
  }, [stream])

  return <video ref={ref} css={style} {...rest} />
}
